/**
 * Environment-variable names are the Proxy Vault's contract with a skill, and
 * validating them is a SECURITY control rather than hygiene.
 *
 * The stamping step writes operator-chosen names into a cell's environment. An
 * operator who names an entry `LD_PRELOAD` does not get a credential — they get
 * arbitrary code execution in the cell, because the dynamic loader will load
 * whatever path that variable names before `main` runs. `PATH`, `PYTHONPATH`,
 * `NODE_OPTIONS` and `BASH_ENV` are the same attack with a different loader.
 *
 * A second family matters just as much here and nowhere else: the variables
 * that decide WHERE a process sends its traffic and WHO it trusts. Letting a
 * connection define `HTTPS_PROXY` or `NODE_EXTRA_CA_CERTS` would hand the cell
 * a way around the very proxy this system depends on — broker bypass, which
 * voids every other guarantee we make.
 *
 * The rule therefore refuses by construction rather than by review, and the
 * same list is used by the console (so the error arrives while typing) and by
 * the server (so the console is not the enforcement point).
 */

/** Names must be SCREAMING_SNAKE and short enough to be legible. */
export const ENV_NAME_PATTERN = /^[A-Z_][A-Z0-9_]{0,63}$/;

/** Reserved for names the platform itself stamps. */
export const RESERVED_PREFIXES = ['SHIFTAGENT_'] as const;

/**
 * Loader, runtime and interpreter hooks. Setting any of these is equivalent to
 * running code in the cell.
 */
const LOADER_DENYLIST = [
  // Dynamic linker (glibc / musl / macOS)
  'LD_PRELOAD', 'LD_LIBRARY_PATH', 'LD_AUDIT', 'LD_DEBUG', 'LD_ORIGIN_PATH',
  'DYLD_INSERT_LIBRARIES', 'DYLD_LIBRARY_PATH', 'DYLD_FRAMEWORK_PATH',
  // Shell
  'PATH', 'IFS', 'BASH_ENV', 'ENV', 'SHELL', 'PS4', 'SHELLOPTS', 'BASHOPTS',
  'CDPATH', 'GLOBIGNORE', 'PROMPT_COMMAND',
  // Python
  'PYTHONPATH', 'PYTHONHOME', 'PYTHONSTARTUP', 'PYTHONEXECUTABLE',
  'PYTHONWARNINGS', 'PYTHONINSPECT', 'PYTHONUSERBASE', 'PYTHONBREAKPOINT',
  // Node
  'NODE_OPTIONS', 'NODE_PATH', 'NODE_REPL_EXTERNAL_MODULE',
  // Other runtimes
  'PERL5LIB', 'PERL5OPT', 'RUBYOPT', 'RUBYLIB', 'GEM_PATH', 'GEM_HOME',
  'CLASSPATH', 'JAVA_TOOL_OPTIONS', '_JAVA_OPTIONS', 'JDK_JAVA_OPTIONS',
  'GOPATH', 'GOROOT', 'DOTNET_STARTUP_HOOKS',
  // Process identity and working environment
  'HOME', 'USER', 'LOGNAME', 'TMPDIR', 'TMP', 'TEMP', 'PWD', 'OLDPWD',
] as const;

/**
 * Egress routing and TLS trust. These are how a cell would route around the
 * proxy or accept a certificate we did not issue.
 */
const EGRESS_DENYLIST = [
  'HTTP_PROXY', 'HTTPS_PROXY', 'ALL_PROXY', 'NO_PROXY', 'FTP_PROXY',
  'http_proxy', 'https_proxy', 'all_proxy', 'no_proxy', 'ftp_proxy',
  'SSL_CERT_FILE', 'SSL_CERT_DIR', 'REQUESTS_CA_BUNDLE', 'CURL_CA_BUNDLE',
  'NODE_EXTRA_CA_CERTS', 'NODE_TLS_REJECT_UNAUTHORIZED',
  'GIT_SSL_CAINFO', 'GIT_SSL_NO_VERIFY', 'GIT_SSH', 'GIT_SSH_COMMAND',
  'AWS_CA_BUNDLE', 'GRPC_DEFAULT_SSL_ROOTS_FILE_PATH',
] as const;

/**
 * Uppercased for comparison, so the lowercase proxy spellings above are caught
 * by the same set. `ENV_NAME_PATTERN` already refuses lowercase names outright;
 * the lowercase entries are listed for the reader, not for the matcher.
 */
const DENYLIST = new Set<string>(
  [...LOADER_DENYLIST, ...EGRESS_DENYLIST].map((n) => n.toUpperCase()),
);

export interface EnvNameValidation {
  valid: boolean;
  error?: string;
}

export function validateEnvName(name: string): EnvNameValidation {
  const raw = name.trim();
  if (!raw) return { valid: false, error: 'environment variable name must not be empty' };

  if (!ENV_NAME_PATTERN.test(raw)) {
    return {
      valid: false,
      error:
        `"${raw}" is not a valid environment variable name — ` +
        'use A-Z, 0-9 and underscore, starting with a letter or underscore, 64 characters or fewer',
    };
  }

  for (const prefix of RESERVED_PREFIXES) {
    if (raw.startsWith(prefix)) {
      return { valid: false, error: `"${prefix}*" is reserved for names the platform stamps` };
    }
  }

  if (DENYLIST.has(raw)) {
    return {
      valid: false,
      error:
        `"${raw}" controls how the cell loads code or where it sends traffic — ` +
        'stamping it would hand over the cell rather than a credential',
    };
  }

  return { valid: true };
}

/** Every name this rule refuses, for tests and for the console's help text. */
export function deniedEnvNames(): string[] {
  return [...DENYLIST].sort();
}
